import api from './api';
import type {
  CipaAcaoContract,
  CipaCandidaturaContract,
  CipaEleitorStatusContract,
  CipaMandatoContract,
  CipaMembroContract,
  CipaProcessoEleitoralContract,
  CipaReuniaoContract,
  ComprovanteVotoContract,
  IndicarBancadaEmpregadorDto,
  InscreverCandidaturaDto,
  RegistrarVotoDto,
} from '@sstexames/shared-types';

export const customerCipaService = {
  /**
   * Obtém o mandato de CIPA ativo para a empresa logada
   */
  async getMandatoAtual(): Promise<CipaMandatoContract | null> {
    const res = await api.get<CipaMandatoContract | null>('/cipa/mandato-atual');
    return res.data;
  },

  /**
   * Lista os membros (bancada do empregador e dos empregados) da CIPA
   */
  async getMembros(mandatoId?: number): Promise<CipaMembroContract[]> {
    const res = await api.get<CipaMembroContract[]>('/cipa/membros', {
      params: mandatoId ? { mandato_id: mandatoId } : undefined,
    });
    return res.data;
  },

  /**
   * Lista o calendário anual e reuniões extraordinárias da CIPA
   */
  async getReunioes(mandatoId?: number): Promise<CipaReuniaoContract[]> {
    const res = await api.get<CipaReuniaoContract[]>('/cipa/reunioes', {
      params: mandatoId ? { mandato_id: mandatoId } : undefined,
    });
    return res.data;
  },

  /**
   * Lista os planos de ação 5W2H sincronizados ao PGR
   */
  async getAcoes(mandatoId?: number): Promise<CipaAcaoContract[]> {
    const res = await api.get<CipaAcaoContract[]>('/cipa/acoes', {
      params: mandatoId ? { mandato_id: mandatoId } : undefined,
    });
    return res.data;
  },

  /**
   * Obtém o processo eleitoral ativo ou em andamento
   */
  async getProcessoEleitoral(mandatoId?: number): Promise<CipaProcessoEleitoralContract | null> {
    const res = await api.get<CipaProcessoEleitoralContract | null>(
      '/cipa/processo-eleitoral',
      {
        params: mandatoId ? { mandato_id: mandatoId } : undefined,
      }
    );
    return res.data;
  },

  /**
   * Permite ao cliente indicar a bancada do empregador diretamente
   */
  async indicarBancadaEmpregador(dto: IndicarBancadaEmpregadorDto): Promise<CipaMembroContract[]> {
    const res = await api.post<CipaMembroContract[]>('/cipa/indicar-bancada', dto);
    return res.data;
  },

  // --- Métodos do Portal do Colaborador (candidaturas e votação digital secreta) ---

  /**
   * Consulta pública do processo eleitoral e lista de candidatos homologados
   */
  async getProcessoPublico(processoId: number): Promise<{
    processo: CipaProcessoEleitoralContract;
    candidatos: CipaCandidaturaContract[];
  }> {
    const res = await api.get<{
      processo: CipaProcessoEleitoralContract;
      candidatos: CipaCandidaturaContract[];
    }>(`/cipa/public/processo/${processoId}`);
    return res.data;
  },

  /**
   * Registra a candidatura do colaborador à CIPA
   */
  async inscreverCandidatura(dto: InscreverCandidaturaDto): Promise<CipaCandidaturaContract> {
    const res = await api.post<CipaCandidaturaContract>('/cipa/public/candidatar', dto);
    return res.data;
  },

  /**
   * Verifica se o colaborador já votou ou se está apto a votar
   */
  async consultarStatusEleitor(
    processoId: number,
    colaboradorId: number
  ): Promise<CipaEleitorStatusContract> {
    const res = await api.get<CipaEleitorStatusContract>('/cipa/public/eleitor-status', {
      params: { processo_id: processoId, colaborador_id: colaboradorId },
    });
    return res.data;
  },

  /**
   * Votação digital na Urna Blindada com emissão de comprovante SHA-256
   */
  async votar(dto: RegistrarVotoDto): Promise<ComprovanteVotoContract> {
    const res = await api.post<ComprovanteVotoContract>('/cipa/public/votar', dto);
    return res.data;
  },

  /**
   * Verifica se a empresa do colaborador possui o módulo gestao_cipa contratado
   */
  async checkEmpresaHasCipa(empresaId: number): Promise<boolean> {
    try {
      const res = await api.get<{ has_cipa: boolean }>(
        '/cipa/public/empresa-has-cipa',
        { params: { empresa_id: empresaId } }
      );
      return Boolean(res.data?.has_cipa);
    } catch {
      return false;
    }
  },
};
